import { createFileRoute } from '@tanstack/react-router'
import {
  BarChart3,
  Blocks,
  CalendarClock,
  Clapperboard,
  CreditCard,
  FlaskConical,
  Layers,
  Loader2,
  ScrollText,
  Tag,
  Users,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { BackofficeCard } from './-components/BackofficeCard'
import { BackofficeStatCard } from './-components/BackofficeStatCard'
import type { BackofficeHealth } from '@/types/backoffice.types'
import { BrandLogo } from '@/components/shared/BrandLogo'
import { RothRadarTargetIcon } from '@/components/shared/RothRadarTargetIcon'
import { PageTitle } from '@/components/PageTitle'
import {
  GoogleDriveIcon,
  HubSpotIcon,
  StripeIcon,
} from '@/components/ExternalLinks'
import { useBackofficeSummary } from '@/hooks/repositories/useBackofficeRepository'
import { formatCurrency } from '@/lib/stripe-formatters'
import { useCan } from '@/hooks/permissions/use-can'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { Department } from '@/types/user.types'
import {
  BACKOFFICE_INTEGRATIONS_PREFIX,
  BACKOFFICE_PERMISSION_KEYS,
  ROTH_RADAR_PREFIX,
} from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute('/_app/backoffice/')({
  component: RouteComponent,
})

// ---- helpers ---------------------------------------------------------------

const healthLabel: Record<BackofficeHealth, string> = {
  healthy: 'Healthy',
  syncing: 'Syncing',
  error: 'Error',
  idle: 'Idle',
}

/** Neutral integration pill: just the company logo + name (no status dot). */
function IntegrationPill({
  label,
  icon,
}: {
  label: string
  icon?: React.ReactNode
}) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium border-border/60 bg-background text-muted-foreground">
      {icon}
      {label}
    </span>
  )
}

function formatRelativeActivity(iso: string | null): string {
  if (!iso) return 'no recent activity'
  try {
    return `last activity ${formatDistanceToNow(new Date(iso), { addSuffix: true })}`
  } catch {
    return 'no recent activity'
  }
}

// ---- component -------------------------------------------------------------

function RouteComponent() {
  const { data, isPending } = useBackofficeSummary()
  const { has, hasAnyWithPrefix, permissionsResolved } = useCan()
  const { user } = useAuth()
  const { interactiveEscapePlan } = useFeatureFlags()
  const isAdmin = user?.department === Department.ADMIN

  // ── Users card ────────────────────────────────────────────────────────────
  const usersCard = has(BACKOFFICE_PERMISSION_KEYS.users) && (
    <BackofficeStatCard
      key="users"
      to="/backoffice/users"
      accentTile="bg-blue-500/10 text-blue-600 dark:text-blue-400"
      Icon={Users}
      title="User Management"
      routeHint="/backoffice/users"
      primaryValue={data?.users.active ?? 0}
      primaryLabel="active users"
      secondaryLine={`${data?.users.newThisWeek ?? 0} new this week`}
      status={{ variant: 'healthy', label: 'Healthy' }}
      isLoading={isPending}
    />
  )

  // ── Billing card ──────────────────────────────────────────────────────────
  const billingNeedsCharge = data?.billing.needsCharge ?? 0
  const billingCard = has(BACKOFFICE_PERMISSION_KEYS.billing) && (
    <BackofficeStatCard
      key="billing"
      to="/backoffice/billing"
      accentTile="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      Icon={CreditCard}
      title="Billing Management"
      routeHint="/backoffice/billing"
      primaryValue={billingNeedsCharge}
      primaryLabel="need charging"
      secondaryLine={`${formatCurrency(data?.billing.queuedAmount ?? null)} queued`}
      status={
        billingNeedsCharge > 0
          ? { variant: 'action', label: 'Action needed' }
          : { variant: 'healthy', label: 'Clear' }
      }
      isLoading={isPending}
    />
  )

  // ── Integrations card ─────────────────────────────────────────────────────
  const syncsRunning = data?.integrations.syncsRunning ?? 0
  const hubspotHealth = data?.integrations.hubspot ?? 'idle'
  const stripeHealth = data?.integrations.stripe ?? 'idle'

  const integrationsStatus = (() => {
    if (hubspotHealth === 'error' || stripeHealth === 'error') {
      return { variant: 'error' as const, label: 'Error' }
    }
    if (syncsRunning > 0)
      return { variant: 'action' as const, label: 'Syncing' }
    if (hubspotHealth === 'healthy' && stripeHealth === 'healthy') {
      return { variant: 'healthy' as const, label: 'Healthy' }
    }
    return { variant: 'idle' as const, label: healthLabel[hubspotHealth] }
  })()

  // Google Drive's pill (like its card on the Integrations page) covers either
  // key it hosts — Recommended Files or the ShareFile Migration tab.
  const showGoogleDrivePill =
    has(BACKOFFICE_PERMISSION_KEYS.googleDrive) ||
    has(BACKOFFICE_PERMISSION_KEYS.sharefileMigration)

  const integrationsCard = hasAnyWithPrefix(BACKOFFICE_INTEGRATIONS_PREFIX) && (
    <BackofficeStatCard
      key="integrations"
      to="/backoffice/integrations"
      accentTile="bg-violet-500/10 text-violet-600 dark:text-violet-400"
      Icon={Blocks}
      title="Integration Management"
      routeHint="/backoffice/integrations"
      primaryValue={syncsRunning}
      primaryLabel="syncs running"
      status={integrationsStatus}
      isLoading={isPending}
      extraContent={
        <div className="flex flex-wrap gap-1.5">
          {has(BACKOFFICE_PERMISSION_KEYS.hubspot) && (
            <IntegrationPill
              label="HubSpot"
              icon={<HubSpotIcon className="size-3 shrink-0" />}
            />
          )}
          {has(BACKOFFICE_PERMISSION_KEYS.stripe) && (
            <IntegrationPill
              label="Stripe"
              icon={<StripeIcon className="size-3 shrink-0" />}
            />
          )}
          {has(BACKOFFICE_PERMISSION_KEYS.mux) && (
            <IntegrationPill
              label="Mux"
              icon={<Clapperboard className="size-3 shrink-0 text-[#fa50b5]" />}
            />
          )}
          {showGoogleDrivePill && (
            <IntegrationPill
              label="Google Drive"
              icon={<GoogleDriveIcon className="size-3 shrink-0" />}
            />
          )}
          {hasAnyWithPrefix(ROTH_RADAR_PREFIX) && (
            <IntegrationPill
              label="RothRadar™"
              icon={
                <RothRadarTargetIcon className="size-3 shrink-0 text-red-600 dark:text-red-400" />
              }
            />
          )}
          {has(BACKOFFICE_PERMISSION_KEYS.plaid) && (
            <IntegrationPill
              label="Plaid"
              icon={
                <BrandLogo domain="plaid.com" name="Plaid" className="size-3" />
              }
            />
          )}
          {has(BACKOFFICE_PERMISSION_KEYS.front) && (
            <IntegrationPill
              label="Front"
              icon={
                <BrandLogo domain="front.com" name="Front" className="size-3" />
              }
            />
          )}
          {has(BACKOFFICE_PERMISSION_KEYS.slack) && (
            <IntegrationPill
              label="Slack"
              icon={
                <BrandLogo domain="slack.com" name="Slack" className="size-3" />
              }
            />
          )}
          {has(BACKOFFICE_PERMISSION_KEYS.meetingTranscripts) && (
            <IntegrationPill
              label="Meeting transcripts"
              icon={
                <span className="flex shrink-0 -space-x-1">
                  <BrandLogo
                    domain="zoom.us"
                    name="Zoom"
                    className="size-3 ring-1 ring-background"
                  />
                  <BrandLogo
                    domain="avoma.com"
                    name="Avoma"
                    className="size-3 ring-1 ring-background"
                  />
                </span>
              }
            />
          )}
        </div>
      }
    />
  )

  // ── Dev Testing card ──────────────────────────────────────────────────────
  const pendingReview = data?.parity.pendingReview ?? 0
  const devTestingCard = has(BACKOFFICE_PERMISSION_KEYS.devTesting) && (
    <BackofficeStatCard
      key="dev-testing"
      to="/backoffice/admin/parity"
      accentTile="bg-amber-500/10 text-amber-600 dark:text-amber-400"
      Icon={FlaskConical}
      title="Dev Testing"
      routeHint="/backoffice/admin/parity"
      primaryValue={pendingReview}
      primaryLabel="pending review"
      secondaryLine={formatRelativeActivity(
        data?.parity.lastActivityAt ?? null,
      )}
      status={
        pendingReview > 0
          ? { variant: 'action', label: 'Needs review' }
          : { variant: 'healthy', label: 'All clear' }
      }
      isLoading={isPending}
    />
  )

  const analyticsCard = has(BACKOFFICE_PERMISSION_KEYS.analytics) && (
    <BackofficeStatCard
      key="analytics"
      to="/backoffice/analytics"
      accentTile="bg-sky-500/10 text-sky-600 dark:text-sky-400"
      Icon={BarChart3}
      title="Internal Reporting and KPIs"
      routeHint="/backoffice/analytics"
      primaryValue={data?.users.active ?? 0}
      primaryLabel="staff reported on"
      secondaryLine="Revenue, pipeline and throughput"
      status={{ variant: 'idle', label: 'Reporting' }}
      isLoading={isPending}
    />
  )

  // Labels is admin-only, while Journey Scheduling (sc-1396) and Terms
  // (sc-1388) each have their own grant. A user whose only Backoffice access
  // is one of those must still get this section, and not the "no access"
  // message the stat cards alone would produce.
  const canJourneyScheduling = has(BACKOFFICE_PERMISSION_KEYS.journeyScheduling)
  const canTerms = has(BACKOFFICE_PERMISSION_KEYS.terms)
  const showConfiguration = isAdmin || canJourneyScheduling || canTerms

  const cards = [
    usersCard,
    billingCard,
    analyticsCard,
    integrationsCard,
    devTestingCard,
  ].filter(Boolean)

  return (
    <div className="py-4">
      <PageTitle title="Backoffice" />
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight">Backoffice</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Operations console — manage users, integrations, billing, and internal
          tooling.
        </p>
      </div>

      {!permissionsResolved ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : cards.length === 0 && !showConfiguration ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          You don't have access to any Backoffice sections.
        </p>
      ) : (
        <>
          {cards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{cards}</div>
          )}

          {/* Configuration - lower-traffic tooling with nothing to roll up,
           * so it uses the plain BackofficeCard rather than
           * BackofficeStatCard. Labels stays admin-only; Journey Scheduling
           * and Terms are each gated on their own grant (ADMIN holds them
           * implicitly through the catalog), so the section itself must
           * render for any of the three. */}
          {showConfiguration && (
            <div className="mt-4">
              <h2 className="text-sm font-semibold text-muted-foreground mb-2">
                Configuration
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isAdmin && (
                  <BackofficeCard
                    title="Labels"
                    description="Rename, recolor, or delete team-wide forecast labels."
                    icon={<Tag className="size-4 text-muted-foreground" />}
                    to="/backoffice/labels"
                  />
                )}
                {canJourneyScheduling && (
                  <BackofficeCard
                    title="Journey Scheduling"
                    description="Default gaps between journey sessions and the business holiday calendar."
                    icon={
                      <CalendarClock className="size-4 text-muted-foreground" />
                    }
                    to="/backoffice/journey-scheduling"
                  />
                )}
                {/* sc-608 round 13 — hidden behind the same flag that gates the
                 * route itself (`interactiveEscapePlan`), so there is no tile
                 * pointing at a page that would just redirect back here. */}
                {isAdmin && interactiveEscapePlan && (
                  <BackofficeCard
                    title="Forecast group names"
                    description="Rename or delete the suggested names planners pick for a client's forecast groups."
                    icon={<Layers className="size-4 text-muted-foreground" />}
                    to="/backoffice/forecast-group-names"
                  />
                )}
                {canTerms && (
                  <BackofficeCard
                    title="Terms"
                    description="Publish and activate the legal terms clients accept in the portal."
                    icon={
                      <ScrollText className="size-4 text-muted-foreground" />
                    }
                    to="/backoffice/terms"
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
